import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  InputAdornment,
  CircularProgress,
  Grid,
  Alert,
  Paper,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Tooltip,
  IconButton,
  Autocomplete,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import RefreshIcon from "@mui/icons-material/Refresh";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import { useSearchParams } from "react-router-dom";
import { useSearchCandidates } from "../../hooks/useSearchCandidates";
import { CandidateCard } from "../../components/cost-estimation/CandidateCard";
import { CandidateDetailsModal, CatalogItemChanges } from "../../components/cost-estimation/CandidateDetailsModal";
import type { Candidate } from "../../services/preventiviApi";
import { searcherApi, BomItemOut, CatalogSourceOption } from "../../services/searcherApi";
import { CATEGORIES, DISCIPLINES } from "../../services/configApi";
import { useAuth } from "../../contexts/AuthContext";
import { UserRole } from "../../generated/api-client";
import toast from "react-hot-toast";

const PAGE_SIZE = 10;

// Convert a BomItemOut to a Candidate-like object for CandidateCard
function bomItemToCandidate(item: BomItemOut): Candidate {
  return {
    uid: item.uid,
    final_score: 1,
    rerank_score: 1,
    match_type: "campione",
    match_percent: null,
    reasoning: "",
    text: item.descrizione_breve ?? item.title,
    metadata: {
      title: item.title,
      description: item.description,
      descrizione_breve: item.descrizione_breve ?? undefined,
      specs: item.specs,
      codice: item.codice ?? undefined,
      prezzo: item.prezzo ?? undefined,
      costo_mat_unitario: item.costo_mat_unitario ?? undefined,
      tempo_posa_ore: item.tempo_posa_ore ?? undefined,
      costo_mano_unitario: item.costo_mano_unitario ?? undefined,
      categoria: item.categoria ?? undefined,
      disciplina: item.disciplina ?? undefined,
      source_file: item.source_file ?? undefined,
      anno: item.anno ?? undefined,
      udm: item.udm ?? undefined,
    },
  } as Candidate;
}

export const ManualSearchPage: React.FC = () => {
  const { user } = useAuth();
  const canEditCatalog = user?.role === UserRole.Admin || user?.role === UserRole.Mantainer;
  const [query, setQuery] = useState("");
  const [categoriaFilter, setCategoriaFilter] = useState("");
  const [disciplinaFilter, setDisciplinaFilter] = useState("");
  const [annoFilter, setAnnoFilter] = useState<string>("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [sourceFilter, setSourceFilterState] = useState<string>(() => searchParams.get("source_file") ?? "");
  const [showFilters, setShowFilters] = useState(() => !!searchParams.get("source_file"));
  const [sourceOptions, setSourceOptions] = useState<CatalogSourceOption[]>([]);

  const { result, loading: searchLoading, error: searchError, search, patchCandidateMetadata, removeCandidate } = useSearchCandidates();

  const loadSourceOptions = useCallback(async () => {
    try {
      setSourceOptions(await searcherApi.listCatalogSources());
    } catch {
      // non-fatal: the file filter just stays empty
    }
  }, []);

  useEffect(() => {
    loadSourceOptions();
  }, [loadSourceOptions]);

  // Keep the file filter in the URL so "Vedi nel catalogo" links and reloads land on the same view.
  const setSourceFilter = useCallback((value: string) => {
    setSourceFilterState(value);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set("source_file", value);
      else next.delete("source_file");
      return next;
    }, { replace: true });
  }, [setSearchParams]);
  // Catalog browse state (random + paginated)
  const [catalogItems, setCatalogItems] = useState<BomItemOut[]>([]);
  const [catalogTotal, setCatalogTotal] = useState(0);
  const [catalogOffset, setCatalogOffset] = useState(0);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [isSearchMode, setIsSearchMode] = useState(false);

  // Detail / edit modal
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailCandidate, setDetailCandidate] = useState<Candidate | null>(null);

  const loadCatalog = useCallback(async (offset = 0, random = false) => {
    setCatalogLoading(true);
    try {
      const res = await searcherApi.listCatalog({
        q: undefined,
        categoria: categoriaFilter || undefined,
        disciplina: disciplinaFilter || undefined,
        anno: annoFilter ? parseInt(annoFilter) : undefined,
        source_file: sourceFilter || undefined,
        random,
        limit: PAGE_SIZE,
        offset,
      });
      if (offset === 0) {
        setCatalogItems(res.items);
      } else {
        setCatalogItems((prev) => [...prev, ...res.items]);
      }
      setCatalogTotal(res.total);
      setCatalogOffset(offset);
    } catch {
      toast.error("Errore nel caricamento del catalogo");
    } finally {
      setCatalogLoading(false);
    }
  }, [categoriaFilter, disciplinaFilter, annoFilter, sourceFilter]);

  // Load 10 random items on mount and when filters change
  useEffect(() => {
    if (!isSearchMode) {
      loadCatalog(0, true);
    }
  }, [categoriaFilter, disciplinaFilter, annoFilter, sourceFilter, isSearchMode, loadCatalog]);

  // Re-run an active search when the file filter changes, so results stay consistent with it.
  const lastSearchSource = React.useRef(sourceFilter);
  useEffect(() => {
    if (lastSearchSource.current === sourceFilter) return;
    lastSearchSource.current = sourceFilter;
    if (isSearchMode && query.trim()) {
      search(query, {
        topK: 8,
        disciplina: (disciplinaFilter as any) || undefined,
        sourceFile: sourceFilter || undefined,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceFilter]);

  const handleSearch = () => {
    if (!query.trim()) {
      setIsSearchMode(false);
      loadCatalog(0, true);
      return;
    }
    setIsSearchMode(true);
    search(query, {
      topK: 8,
      disciplina: (disciplinaFilter as any) || undefined,
      sourceFile: sourceFilter || undefined,
    });
  };

  const handleRefreshRandom = () => {
    setIsSearchMode(false);
    setQuery("");
    loadCatalog(0, true);
  };

  const handleLoadMore = () => {
    loadCatalog(catalogOffset + PAGE_SIZE, false);
  };

  const handleShowDetail = (c: Candidate) => {
    setDetailCandidate(c);
    setDetailOpen(true);
  };

  const handleSaveBomItem = async (uid: string, changes: CatalogItemChanges) => {
    try {
      await searcherApi.updateBomItem(uid, changes);
      const metaPatch: Record<string, any> = { ...changes };
      patchCandidateMetadata(uid, metaPatch);
      setDetailCandidate((prev) =>
        prev && prev.uid === uid ? { ...prev, metadata: { ...prev.metadata, ...metaPatch } } : prev
      );
      // Also refresh catalog
      setCatalogItems((prev) => prev.map((item) => item.uid === uid ? { ...item, ...changes } : item));
      toast.success("Metadati aggiornati nel catalogo");
    } catch (err: any) {
      toast.error(err?.message ?? err?.response?.data?.detail ?? "Errore nel salvataggio");
      throw err;
    }
  };

  const handleDeleteBomItem = async (uid: string) => {
    try {
      await searcherApi.deleteBomItem(uid);
      setCatalogItems((prev) => prev.filter((item) => item.uid !== uid));
      setCatalogTotal((t) => Math.max(0, t - 1));
      removeCandidate(uid);
      setDetailOpen(false);
      setDetailCandidate(null);
      loadSourceOptions();
      toast.success("Articolo eliminato dal catalogo");
    } catch (err: any) {
      toast.error(err?.response?.data?.detail ?? "Errore durante l'eliminazione");
      throw err;
    }
  };

  // ── Determine displayed items ──────────────────────────────────────────────
  const displayCandidates: Candidate[] = isSearchMode
    ? (result?.candidates ?? []).filter((c) => c.match_type !== "excluded")
    : catalogItems.map(bomItemToCandidate);

  const activeFilterCount = [categoriaFilter, disciplinaFilter, annoFilter, sourceFilter].filter(Boolean).length;
  const selectedSourceOption =
    sourceOptions.find((o) => o.source_file === sourceFilter) ??
    (sourceFilter ? { source_file: sourceFilter, item_count: 0 } : null);
  const isRandomSample = !isSearchMode && activeFilterCount === 0;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 920, mx: "auto", width: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.25 }}>
            Catalogo articoli
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Sfoglia, cerca e modifica gli articoli nel database.
          </Typography>
        </Box>
        <Tooltip title="Aggiorna risultati random">
          <IconButton size="small" onClick={handleRefreshRandom}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Search bar + filter toggle */}
      <Box sx={{ display: "flex", gap: 1, mb: 1.5 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Cerca nel catalogo (es. motoriduttore 24V flangiato)..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "text.disabled", fontSize: "1.1rem" }} />
                </InputAdornment>
              ),
              sx: { borderRadius: "10px" },
            },
          }}
        />
        <Button
          variant="contained"
          onClick={handleSearch}
          disabled={searchLoading}
          sx={{ borderRadius: "10px", px: 3, flexShrink: 0 }}
        >
          {searchLoading ? <CircularProgress size={20} /> : "Cerca"}
        </Button>
        <Tooltip title="Filtri">
          <IconButton
            onClick={() => setShowFilters((v) => !v)}
            sx={{
              border: "1px solid",
              borderColor: showFilters ? "primary.main" : "divider",
              borderRadius: "10px",
              bgcolor: showFilters ? "primary.light" : "transparent",
              color: showFilters ? "primary.main" : "text.secondary",
            }}
          >
            <TuneIcon fontSize="small" />
            {activeFilterCount > 0 && (
              <Box
                sx={{
                  position: "absolute", top: 4, right: 4, width: 8, height: 8,
                  borderRadius: "50%", bgcolor: "error.main",
                }}
              />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Filters panel */}
      {showFilters && (
        <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: "12px", p: 2, mb: 2 }}>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Categoria</InputLabel>
              <Select
                value={categoriaFilter}
                label="Categoria"
                onChange={(e) => setCategoriaFilter(e.target.value)}
              >
                <MenuItem value=""><em>Tutte</em></MenuItem>
                {CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Disciplina</InputLabel>
              <Select
                value={disciplinaFilter}
                label="Disciplina"
                onChange={(e) => setDisciplinaFilter(e.target.value)}
              >
                <MenuItem value=""><em>Tutte</em></MenuItem>
                {DISCIPLINES.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField
              label="Anno"
              value={annoFilter}
              onChange={(e) => setAnnoFilter(e.target.value)}
              size="small"
              type="number"
              sx={{ width: 100 }}
              placeholder="es. 2025"
            />
            <Autocomplete
              size="small"
              options={sourceOptions}
              value={selectedSourceOption}
              onChange={(_, opt) => setSourceFilter(opt?.source_file ?? "")}
              getOptionLabel={(o) => o.source_file}
              isOptionEqualToValue={(a, b) => a.source_file === b.source_file}
              noOptionsText="Nessun file trovato"
              sx={{ flex: 1, minWidth: 260 }}
              slotProps={{ popper: { placement: "bottom-end", sx: { minWidth: 440 } } }}
              renderOption={({ key, ...props }, o) => (
                <Box component="li" key={key} {...props} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <FolderOpenIcon sx={{ fontSize: "0.95rem", color: "text.disabled", flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ flex: 1, minWidth: 0, overflowWrap: "anywhere", fontSize: "0.82rem" }}>
                    {o.source_file}
                  </Typography>
                  <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>
                    {o.item_count.toLocaleString("it-IT")}
                  </Typography>
                </Box>
              )}
              renderInput={(params) => (
                <TextField {...params} label="File di origine" placeholder="Tutti i file" />
              )}
            />
            {activeFilterCount > 0 && (
              <Button
                size="small"
                variant="text"
                onClick={() => { setCategoriaFilter(""); setDisciplinaFilter(""); setAnnoFilter(""); setSourceFilter(""); }}
              >
                Azzera filtri
              </Button>
            )}
          </Box>
        </Paper>
      )}

      {searchError && <Alert severity="error" sx={{ mb: 2 }}>{searchError}</Alert>}

      {/* Results area */}
      {(searchLoading || catalogLoading) && displayCandidates.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          {/* Header bar */}
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {isSearchMode
                  ? `${displayCandidates.length} risultati per "${query}"`
                  : `${catalogTotal.toLocaleString("it-IT")} ${activeFilterCount > 0 ? "articoli filtrati" : "articoli in catalogo"}`}
              </Typography>
              {isRandomSample && (
                <Chip label="Campione casuale" size="small" sx={{ height: 18, fontSize: "0.65rem" }} />
              )}
              {sourceFilter && (
                <Tooltip title={sourceFilter}>
                  <Chip
                    icon={<FolderOpenIcon sx={{ fontSize: "0.8rem !important" }} />}
                    label={sourceFilter}
                    size="small"
                    onDelete={() => setSourceFilter("")}
                    sx={{ height: 20, fontSize: "0.68rem", maxWidth: 360, bgcolor: "#E3F2FD", color: "#1565C0" }}
                  />
                </Tooltip>
              )}
              {isSearchMode && result && result.candidates.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  — confidenza {(result.candidates[0].match_percent ?? 0).toFixed(0)}%
                </Typography>
              )}
            </Box>
            {isSearchMode && (
              <Button size="small" variant="text" onClick={handleRefreshRandom}>
                ← Sfoglia catalogo
              </Button>
            )}
          </Box>

          {displayCandidates.length === 0 ? (
            <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: "12px", p: 4, textAlign: "center" }}>
              <Typography color="text.secondary">
                {isSearchMode
                  ? "Nessun risultato trovato."
                  : activeFilterCount > 0
                    ? "Nessun articolo per i filtri selezionati."
                    : "Catalogo vuoto."}
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={1.5}>
              {displayCandidates.map((c) => (
                <Grid size={12} key={c.uid}>
                  <CandidateCard
                    candidate={c}
                    showChooseButton={false}
                    onDetails={() => handleShowDetail(c)}
                  />
                </Grid>
              ))}
            </Grid>
          )}

          {/* Pagination: load more (only in browse mode) */}
          {!isSearchMode && catalogItems.length < catalogTotal && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
              <Button
                variant="outlined"
                onClick={handleLoadMore}
                disabled={catalogLoading}
                sx={{ borderRadius: "20px" }}
              >
                {catalogLoading ? <CircularProgress size={20} /> : `Carica altri 10 (${catalogTotal - catalogItems.length} rimanenti)`}
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* Detail modal — full edit only for admin/maintainer */}
      <CandidateDetailsModal
        candidate={detailCandidate}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        editable={canEditCatalog}
        onSave={canEditCatalog ? handleSaveBomItem : undefined}
        onDelete={canEditCatalog ? handleDeleteBomItem : undefined}
        queryText={isSearchMode ? query : undefined}
      />

    </Box>
  );
};
